import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTermsOfService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Scale, ArrowLeft, Loader2 } from 'lucide-react';

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await getTermsOfService();
        setTerms(res.data);
      } catch (err) {
        console.error('Failed to load ToS:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, i) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-2xl font-bold mt-6 mb-4">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-semibold mt-5 mb-3">{line.slice(3)}</h2>;
      }
      // Bold text
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold mt-4 mb-2">{line.slice(2, -2)}</p>;
      }
      // List items
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-4 text-muted-foreground">{line.slice(2)}</li>;
      }
      // Horizontal rule
      if (line === '---') {
        return <hr key={i} className="my-6 border-border" />;
      }
      // Empty line
      if (line.trim() === '') {
        return <br key={i} />;
      }
      // Regular paragraph
      return <p key={i} className="text-muted-foreground leading-relaxed">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-navy flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">Legal AIDynamics</span>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">
              {terms?.title || 'Terms of Service'}
            </CardTitle>
            {terms?.last_updated && (
              <p className="text-sm text-muted-foreground">Last updated: {terms.last_updated}</p>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-bronze" />
              </div>
            ) : (
              <ScrollArea className="h-[60vh] pr-4">
                <div className="prose prose-sm max-w-none">
                  {renderContent(terms?.content)}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
}
